import React, { Dispatch, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import cx from 'classnames';
import { chain, mapValues, pickBy } from 'lodash';
import { Button, Tag, Intent, Spinner } from '@blueprintjs/core';
import { useLocation } from 'react-router-dom';
import { DatasetStatus } from '@retro-ui/api-contract';

import Table from '@f/components/shared/table/Table';
import RemovalTooltip from '@f/components/shared/removal-tooltip/RemovalTooltip';
import { removeDatasets } from '@f/services/starting-material/starting-material.actions';
import {
  StartingMaterialDatasetItem,
  MapDatasetItem,
  StartingMaterialReducer,
} from '@f/services/starting-material/starting-material.types';
import { formatBytes } from '@f/utils/text';

import Collapse from '../components/Collapse';

import cls from './DatasetList.module.scss';

export interface DatasetListProps {
  onSelectionChange: (itemIds: string[]) => void;
  onDatasetsTotalSizeChange: (bytesUsed: number) => void;
}

interface ConditionalMap {
  [itemId: string]: boolean;
}

interface CellProps {
  className: string;
}

interface RowProps {
  className: string;
  after?: React.ReactElement;
}

interface ColumnSchema {
  label: string | MapDatasetItem<React.ReactElement>;
  renderer: string | MapDatasetItem<string> | MapDatasetItem<React.ReactElement>;
  grow?: boolean;
  cellProps?: CellProps | MapDatasetItem<CellProps>;
}

interface TableSchema {
  [columnIdx: number]: ColumnSchema;
}

const buildTableSchema = (
  props: DatasetListProps,
  dispatch: Dispatch<any>,
  onRowClick: (item: StartingMaterialDatasetItem) => void
): TableSchema => [
  {
    label: 'TITLE',
    grow: true,
    renderer: (item): string => item.name || 'Unnamed',
    cellProps: (item): CellProps => ({
      className: cx(cls.primary, { [cls.italic]: !item.name }),
    }),
  },
  {
    label: 'STATUS',
    renderer: (item): React.ReactElement => {
      switch (item.status) {
        case DatasetStatus.READY:
          return (
            <Tag large minimal round intent={Intent.SUCCESS} icon="tick">
              Available
            </Tag>
          );
        case DatasetStatus.PROCESSING: {
          return (
            <Tag large minimal round intent={Intent.WARNING} icon="cloud">
              Finalising
            </Tag>
          );
        }
        case DatasetStatus.SENDING: {
          const progress = item.uploadedSize / item.size;

          return (
            <Tag
              large
              minimal
              round
              intent={Intent.WARNING}
              icon={<Spinner size={16} intent={Intent.WARNING} value={progress} />}
            >
              Uploading {Number(progress * 100).toFixed(1)}%
            </Tag>
          );
        }
        case DatasetStatus.INCOMPLETE:
          return (
            <Tag large minimal round intent={Intent.PRIMARY} icon="outdated">
              Upload paused
            </Tag>
          );
        case DatasetStatus.CANCELED:
          return (
            <Tag large minimal round intent={Intent.NONE} icon="disable">
              Upload canceled
            </Tag>
          );
        default:
          return (
            <Tag large minimal round intent={Intent.DANGER} icon="error">
              Upload failed
            </Tag>
          );
      }
    },
  },
  {
    label: (): React.ReactElement => <span className={cls.datasetSizeLabel}>SIZE (GB)</span>,
    renderer: (item): React.ReactElement => (
      <span className={cls.datasetSizeValue}>{formatBytes(item.size, { unit: 'GB' })}</span>
    ),
  },
  {
    label: '',
    renderer: (item): React.ReactElement => (
      <Button small minimal icon="more" onClick={() => onRowClick(item)} />
    ),
  },
  {
    label: '',
    renderer: (item): React.ReactElement => (
      <RemovalTooltip
        renderTarget={props => (
          <Button
            small
            minimal
            icon="trash"
            disabled={item.status === DatasetStatus.CANCELED}
            onClick={props.onClick}
            data-cy="button-sm-dataset-remove-one"
          />
        )}
        onRemove={() => {
          dispatch(removeDatasets([item.id]));
        }}
      />
    ),
  },
];

function buildRowProps(expansionMap: ConditionalMap) {
  return (item: StartingMaterialDatasetItem): RowProps => {
    const result: RowProps = {
      className: cx({
        [cls.expanded]: expansionMap[item.id],
      }),
    };

    if (expansionMap[item.id]) {
      result.after = <Collapse item={item} />;
    }

    return result;
  };
}

export default function (props: DatasetListProps) {
  const location = useLocation();
  const data = useSelector((state: any): StartingMaterialReducer['dataset']['data'] => {
    const startingMaterial = state.startingMaterial as StartingMaterialReducer;
    return pickBy(
      startingMaterial.dataset.data,
      dataset => dataset.isPrivate && dataset.status !== DatasetStatus.REMOVED
    );
  });
  const isFinding = useSelector((state: any) => {
    const startingMaterial = state.startingMaterial as StartingMaterialReducer;
    return startingMaterial.dataset.find.processing > 0;
  });
  const dispatch = useDispatch();
  const [selectionMap, setSelectionMap] = React.useState<ConditionalMap>({});
  const [expansionMap, setExpansionMap] = React.useState<ConditionalMap>({});

  useEffect(() => {
    const sumSizes = Object.values(data)
      .filter(item => item.status !== DatasetStatus.CANCELED)
      .reduce((acc, item) => acc + item.size, 0);
    props.onDatasetsTotalSizeChange(sumSizes);
  }, [data]);

  useEffect(() => {
    if (!location.search) {
      return;
    }
    const searchParams = new URLSearchParams(location.search);
    const openDataset = searchParams.get('openDataset');
    if (!openDataset) {
      return;
    }
    setExpansionMap(prevExpansionMap => ({ ...prevExpansionMap, [openDataset]: true }));
  }, [location]);

  const items = chain(data).values().sortBy('createdAt').reverse().value();

  const isRowSelected = (item: StartingMaterialDatasetItem): boolean => selectionMap[item.id];
  const onRowSelectionChange: MapDatasetItem<void> = item => {
    setSelectionMap(prevSelectionMap => ({
      ...prevSelectionMap,
      [item.id]: !prevSelectionMap[item.id],
    }));
  };
  const onAllRowsSelectionChange = (): void => {
    setSelectionMap(prevSelectionMap => {
      const areAllSelected = Object.values(data).every(item => Boolean(prevSelectionMap[item.id]));

      // If all items are selected, deselect all
      if (areAllSelected) {
        return {};
      }

      return {
        ...prevSelectionMap,
        ...mapValues(data, () => true),
      };
    });
  };

  const onRowClick: (item: StartingMaterialDatasetItem) => void = React.useCallback(
    item => {
      setExpansionMap({
        ...expansionMap,
        [item.id]: !expansionMap[item.id],
      });
    },
    [expansionMap]
  );
  const rowProps = React.useCallback(buildRowProps(expansionMap), [JSON.stringify(expansionMap)]);

  React.useEffect(() => {
    const { onSelectionChange } = props;

    const selectedItemIds = chain(selectionMap).pickBy(Boolean).keys().value();
    onSelectionChange(selectedItemIds);
  }, [JSON.stringify(selectionMap)]);

  return (
    <Table
      className={cls.list}
      schema={buildTableSchema(props, dispatch, onRowClick)}
      items={items}
      // selectable
      isRowSelected={isRowSelected}
      loading={isFinding}
      loadingCaption="Loading datasets..."
      rowProps={rowProps}
      emptyStateProps={{
        title: 'No datasets',
        description: "You haven't uploaded any datasets yet.",
      }}
      onRowClick={onRowClick}
      onRowSelectionChange={onRowSelectionChange}
      onAllRowsSelectionChange={onAllRowsSelectionChange}
      data-cy="table-sm-datasets"
    />
  );
}
